import { motion } from 'framer-motion';
import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import { FAQ_ITEMS, type FaqItem } from '../../data/faqData';

interface FAQSectionProps {
  faqItems?: FaqItem[];
  heading?: string;
  headingHighlight?: string;
  subtitle?: string;
  contactLinkText?: string;
  contactSuffix?: string;
}

export function FAQSection({
  faqItems = FAQ_ITEMS,
  heading = 'Frequently asked',
  headingHighlight = 'questions',
  subtitle = "Have questions? We've got answers. Can't find what you're looking for?",
  contactLinkText = 'Get in touch',
  contactSuffix = '.',
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-20 md:py-32 bg-background-card">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            FAQ
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
            style={displayFont}
          >
            {heading} <span className="text-primary">{headingHighlight}</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto"
          >
            {subtitle}{' '}
            <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">
              {contactLinkText}
            </a>
            {contactSuffix}
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-4"
        >
          {faqItems.map((faq, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-background border border-border rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-background-elevated transition-colors"
                aria-expanded={openIndex === index}
              >
                <span className="font-semibold text-text-primary pr-8">{faq.question}</span>
                {openIndex === index ? (
                  <Minus className="w-5 h-5 text-primary flex-shrink-0" />
                ) : (
                  <Plus className="w-5 h-5 text-text-secondary flex-shrink-0" />
                )}
              </button>
              {/* Always rendered in DOM so SSR includes answer text for crawlers.
                  Framer Motion animates height/opacity on the client. */}
              <motion.div
                animate={openIndex === index
                  ? { height: 'auto', opacity: 1 }
                  : { height: 0, opacity: 0 }}
                initial={false}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <div className="px-6 pb-6 text-text-secondary">{faq.answer}</div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
